import { useCallback, useEffect, useRef, useState } from 'react';

export type InlineFeedbackKind = 'success' | 'error' | 'warning' | 'info';

export interface InlineFeedbackState {
  show: boolean;
  type: InlineFeedbackKind;
  message: string;
}

export interface InlineFeedbackScope {
  emit: (type: InlineFeedbackKind, message: string, autoHideMs?: number) => void;
  resetTimer: () => void;
  clear: () => void;
}

export function useInlineFeedbackScope(defaultAutoHideMs = 5000) {
  const [feedback, setFeedback] = useState<InlineFeedbackState>({
    show: false,
    type: 'info',
    message: '',
  });

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentAutoHideRef = useRef<number>(defaultAutoHideMs);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(
    (ms: number) => {
      clearTimer();
      if (ms <= 0) return;
      timerRef.current = setTimeout(() => {
        setFeedback((prev) => ({ ...prev, show: false }));
      }, ms);
    },
    [clearTimer]
  );

  const emit = useCallback(
    (type: InlineFeedbackKind, message: string, autoHideMs = defaultAutoHideMs) => {
      currentAutoHideRef.current = autoHideMs;
      setFeedback({ show: true, type, message });
      startTimer(autoHideMs);
    },
    [defaultAutoHideMs, startTimer]
  );

  const clear = useCallback(() => {
    clearTimer();
    setFeedback((prev) => ({ ...prev, show: false }));
  }, [clearTimer]);

  const resetTimer = useCallback(() => {
    if (!feedback.show) return;
    startTimer(currentAutoHideRef.current);
  }, [feedback.show, startTimer]);

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  const scope: InlineFeedbackScope = { emit, resetTimer, clear };

  return { feedback, scope, emit, clear, resetTimer };
}

